import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ChatGroup } from '@models/Chat/ChatGroup';
import { GroupMsg } from '@models/Chat/GroupMsg';
import { CurrentUser } from '@models/Core/CurrentUser';
import { User } from '@models/Core/User';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { AuthenticationService } from '@services/auth/authentication.service';
import { ConfigService } from '@services/config.service';
import { GroupChatService } from '@services/groupchat/groupchat.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { GroupChatSocketService } from '@services/sockets/groupchat.socket.service';
import { linkify } from '@utils/linkify';
import { Subscription } from 'rxjs';
import { zip } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IdeaModalComponent } from '../sessions/single/idea-modal/idea-modal.component';

@Component({
  selector: 'app-groupchat',
  templateUrl: './groupchat.component.html',
  styleUrls: ['./groupchat.component.scss'],
})
export class GroupchatComponent implements OnInit, OnDestroy {
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() general: boolean;
  @ViewChild('messagePane', { static: true }) messagePane: ElementRef;

  public pretitle: string;
  public title: string;
  public image: string;
  public group: ChatGroup;
  public messages: GroupMsg[] = [];
  public users: User[] = [];
  public me: CurrentUser;

  public newMsg = '';
  sending = false;
  private sub = new Subscription();

  constructor(
    private service: GroupChatService,
    private appConfig: ConfigService,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private socket: GroupChatSocketService,
    private sessionReq: SessionsRequestService,
    private router: Router,
    private auth: AuthenticationService,
    private assetsPipe: AssetsPipe,
  ) {}

  ngOnInit() {
    this.auth.getCurrentUser.subscribe(m => (this.me = m));

    if (this.session) {
      if (this.idea) {
        this.pretitle = 'Discussion on Idea';
        this.title = this.idea.title;
        this.image = this.assetsPipe.transform(
          this.idea.thumbnail_id || 'image/default_idea_thumbnail.png',
        );
        this.users = this.session.config.participants
          .map(p => p.user)
          .filter(u => !!u);

        zip(
          this.service.getGroupByIdea(this.session, this.idea),
          this.sessionReq.getSingle(this.session._id),
        ).subscribe(([group, session]) => {
          this.group = group;
          this.session = session;
          this.users = this.session.config.participants
            .map(p => p.user)
            .filter(u => !!u);
          this.fetchMessages();
        });
      } else {
        if (this.session.config.cluster) {
          this.pretitle = 'Discussion on Topic Cluster';
        } else {
          this.pretitle = 'Discussion on Topic';
        }
        this.title = this.session.config.title;
        this.image = this.assetsPipe.transform(
          this.session.config.thumbnail_id ||
            'image/default_session_thumbnail.png',
        );
        this.users = this.session.config.participants
          .map(p => p.user)
          .filter(u => !!u);

        this.service.getGroupBySession(this.session).subscribe(group => {
          this.group = group;
          this.fetchMessages();
        });
      }
    } else if (this.general) {
      this.pretitle = 'Messages from';
      this.title = 'Admins';
      this.image =
        'https://eit1-staging.wedavinci.com/assets/images/direct-msg-empty.svg';

      zip(
        this.service.getGeneralGroup(),
        this.service.getGeneralGroupPosters(),
      ).subscribe(([group, users]) => {
        this.group = group;
        this.users = users;
        this.fetchMessages();
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  fetchMessages() {
    this.service.messages(this.group).subscribe(messages => {
      this.messages = messages;
      messages.forEach(msg => {
        msg.created_by = this.sender(msg);
      });

      this.scrollToBottom();
    });

    this.sub.add(
      this.socket
        .onNewMessage(this.group)
        .pipe(tap(msg => (msg.created_by = this.sender(msg))))
        .subscribe(msg => {
          this.messages.push(msg);
          this.scrollToBottom();
        }),
    );

    this.sub.add(
      this.socket.onDeleteMessage(this.group).subscribe(msg_id => {
        this.messages = this.messages.filter(m => m._id.toString() !== msg_id);
      }),
    );
  }

  scrollToBottom() {
    setTimeout(
      () =>
        this.messagePane.nativeElement.scrollTo({
          top: this.messagePane.nativeElement.scrollHeight,
          behavior: 'smooth',
        }),
      30,
    );
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  lines(msg: GroupMsg) {
    return msg.text
      .trim()
      .split('\n')
      .map(line => linkify(line.trim()));
  }

  showDate(index: number) {
    if (index > 0) {
      const msg = this.messages[index];
      if (msg.announcement) {
        return true;
      }

      const prev = this.messages[index - 1];
      const diff = (msg.created_at as any) - (prev.created_at as any);
      if (
        msg.created_by === prev.created_by &&
        msg.announcement === prev.announcement &&
        diff < 4 * 60 * 1000
      ) {
        return false;
      }
    }

    return true;
  }

  showSender(index: number) {
    if (index > 0) {
      const msg = this.messages[index];
      if (msg.announcement) {
        return true;
      }

      const prev = this.messages[index - 1];
      const diff = (msg.created_at as any) - (prev.created_at as any);
      if (
        msg.created_by === prev.created_by &&
        msg.announcement === prev.announcement &&
        diff < 30 * 60 * 1000
      ) {
        return false;
      }
    }

    return true;
  }

  canPost() {
    if (!this.group) {
      return false;
    }

    if (this.general) {
      return this.auth.canPostInGeneral();
    }

    return true;
  }

  canAnnounce() {
    if (!this.group) {
      return false;
    }

    if (this.group.meta.type === 'session') {
      return this.session.me.role <= this.appConfig.roles.SESSION_MODERATOR;
    } else if (this.group.meta.type === 'idea') {
      return this.session.me._id.toString() === this.idea.created_by.toString();
    } else if (this.general) {
      return this.auth.canPostInGeneral();
    } else {
      return false;
    }
  }

  sender(msg: GroupMsg) {
    return this.users.find(u => u._id.toString() === msg.created_by);
  }

  mine(msg: GroupMsg) {
    if (typeof msg.created_by === 'string') {
      return msg.created_by === this.me._id.toString();
    } else {
      return msg.created_by._id.toString() === this.me._id.toString();
    }
  }

  delete(msg: GroupMsg) {
    this.service.delete(this.group, msg).subscribe(() => {
      // this.messages.splice(this.messages.indexOf(msg), 1);
    });
  }

  public send() {
    this.sending = true;
    this.service.send(this.group, this.newMsg).subscribe(() => {
      this.newMsg = '';
      this.sending = false;
      this.scrollToBottom();
    });
  }

  public announce() {
    this.sending = true;
    this.service.announce(this.group, this.newMsg).subscribe(() => {
      this.newMsg = '';
      this.sending = false;
      this.scrollToBottom();
    });
  }

  navigate() {
    if (this.group.meta.type === 'session') {
      this.dismiss();
      this.router.navigate(['/topics/' + this.session._id]);
    } else if (this.group.meta.type === 'idea') {
      this.dismiss();
      const ref = this.modalService.open(IdeaModalComponent, {
        size: 'lg',
        windowClass: 'big-modal',
      });
      ref.componentInstance.bindData({
        session: this.session,
        idea: this.idea,
      });
    }
  }
}
